import React, { forwardRef, useCallback } from 'react'
import { DomainNotificationsProvider } from '../../../providers/domain-notifications-provider/domain-notifications-provider'
import { useLocation, useParams } from 'react-router-dom'
import { OrgNotificationModel } from '../../../models/notification/org-notification.model'
import { NotificationDto } from '../../../features/notifications'
import { useService } from '@pushly/aqe/lib/hooks'
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect'
import { AppState } from '../../../stores/app'
import { DomainNotificationBuilderViewProps, DomainNotificationBuilderViewInternals } from './internals'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { getVariantDisplayLabel } from '../helpers'
import { VariantManagerProvider } from '@pushly/aqe/lib/components'
import { DomainNotificationTemplateProvider } from '../../../providers/domain-notification-template-provider/domain-notification-template-provider'
import { NotificationBuilderRef } from '../notification-builder'
import { useURLSearchParams } from '../../../hooks/use-url-search-params'
import { DomainDto } from '../../../dtos/domain'
import { AppService } from '../../../services'
import { useNotificationBuilderViewResourceFetchErrorCallback } from './internals/hooks/use-notification-builder-view-resource-fetch-error-callback'

type NotificationBuilderViewPathParams = {
    domainId?: string
    notifId?: string
    notificationId?: string
    templateId?: string
}

export const DomainNotificationBuilderView = forwardRef<NotificationBuilderRef, DomainNotificationBuilderViewProps>(
    (props, ref) => {
        const appState = useService(AppState)

        const {
            domainId: domainIdPathParam,
            notifId: notifIdPathParam,
            notificationId: notificationIdPathParam,
            templateId: templateIdPathParam,
        } = useParams<NotificationBuilderViewPathParams>()

        const { state: routeState } = useLocation<{
            notification?: OrgNotificationModel | NotificationDto
        }>()

        const handleNotificationsFetchError = useNotificationBuilderViewResourceFetchErrorCallback({
            level: 'domain',
            domainId: domainIdPathParam,
            resolveParentRoute: props.getParentRoute,
            onFetchError: props.onNotificationsFetchError,
        })

        const handleNamedTemplateFetchError = useNotificationBuilderViewResourceFetchErrorCallback({
            level: 'domain',
            domainId: domainIdPathParam,
            resolveParentRoute: props.getParentRoute,
            onFetchError: props.onNamedTemplateFetchError,
        })

        const handleActiveDomainChange = useCallback(
            (domain: DomainDto, appSvc: AppService) => {
                if (props.onActiveDomainChange) {
                    return props.onActiveDomainChange(domain, appSvc)
                }

                const route = props.getParentRoute
                    ? props.getParentRoute(false) // do we need to know if it's a draft for this call?
                    : `/domains/${domainIdPathParam}/notifications`

                appSvc.route(route)
            },
            [props.onActiveDomainChange, props.getParentRoute],
        )

        useActiveDomainChangeEffect(handleActiveDomainChange)

        const searchParams = useURLSearchParams()
        const duplicateSourceIdQueryParam = searchParams.get('notification_id')
        const templateIdQueryParam = searchParams.get('template_id')
        const testIdQueryParam = searchParams.get('test')

        const notificationPathId = notifIdPathParam ?? notificationIdPathParam
        const notificationSourceId = notificationPathId ?? duplicateSourceIdQueryParam
        const templateSourceId = templateIdPathParam ?? templateIdQueryParam

        if (!domainIdPathParam) {
            throw new Error('Missing domainId')
        }
        const domainId = Number(domainIdPathParam)

        if (!appState.currentDomain) {
            // Rendering should be prevented until the appState has fully loaded.
            return null
        }

        return (
            <DomainNotificationsProvider
                domainId={domainId}
                notificationId={notificationSourceId}
                testId={testIdQueryParam}
                notification={routeState?.notification}
                onFetchError={handleNotificationsFetchError}
            >
                <DomainNotificationTemplateProvider
                    domainId={domainId}
                    templateId={templateSourceId}
                    onFetchError={handleNamedTemplateFetchError}
                >
                    <VariantManagerProvider<NotificationVariantModel>
                        getVariantIdentity={(v) => v.getId()}
                        getVariantLabel={(_v, variantIdx, totalVariants) =>
                            getVariantDisplayLabel(variantIdx, totalVariants ?? 1)
                        }
                    >
                        <DomainNotificationBuilderViewInternals
                            ref={ref}
                            {...props}
                            levelId={domainId}
                            requestedNotifId={notificationPathId ? Number(notificationPathId) : undefined}
                            requestedTestId={testIdQueryParam ? Number(testIdQueryParam) : undefined}
                            requestedTemplateId={templateIdPathParam ? Number(templateIdPathParam) : undefined}
                        />
                    </VariantManagerProvider>
                </DomainNotificationTemplateProvider>
            </DomainNotificationsProvider>
        )
    },
)
